import axios from 'axios';
import {
  Trial,
  SocRegimenGroup,
  SocRegimenReference,
  NCCNStatus,
  IndicationInfo,
  RecentIndication,
  ClinicalDashboardState,
  EmergingIndication,
  RecentEmergingIndication,
  EmergingDataRow,
  EmergingSourceRow,
  EmergingTxAuditEntry,
  Catalyst,
  CatalystFavorite,
  EmergingAssetSummary,
  EmergingCompanyAssetsResponse,
  EmergingTrialDetail,
  EmergingScrapeSummary,
  AlertDigestSubscription,
  UnifiedPlatformMergeResponse,
  ArDisease,
  ArWorkbookParsed,
  ArTrackerAsset,
  ArIndexAllEntry,
  ArList,
  ArListKind,
  ArListsResponse,
  ArTimelineResponse,
  ArRegistryBatchResponse,
  ArRegistryDetailResponse,
  ArGeoWmap,
  ArGeoLookups,
  ArGeoHi,
  ArGeoCityRaw,
  ArGeoCC,
} from '../types';
import { resolveG35Ae } from '../utils/g35Ae';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

// Session-scoped cache for the emerging indications list (same data for entire session).
let _emergingIndicationsCache: EmergingIndication[] | null = null;
let _emergingIndicationsFetch: Promise<EmergingIndication[]> | null = null;

// Backend data structure (snake_case from ruby_data + pmid_info + pmid_map)
interface BackendTrial {
  id?: number;
  identifier?: string;
  pmid?: string | number;
  trial_name?: string;
  nct_id?: string;
  doi?: string;
  trial_arm_notes?: string;
  indication_details?: string;
  primary_endpoint?: string;
  secondary_endpoints?: string;
  median_follow_up_time_months?: number | string | null;
  baseline_characteristics?: string;
  biomarkers_info?: string;
  other_efficacy?: string;
  other_info_about_aes?: string;
  nccn_notes?: string;
  preferred_status?: string;
  experimental_regimen?: string;
  control_regimen?: string;
  exp_n?: number;
  control_n?: number;
  orr_exp?: number | null;
  orr_control?: number | null;
  cr_exp?: number | null;
  cr_control?: number | null;
  mpfs_exp_mos?: number | null;
  mpfs_control_mos?: number | null;
  pfs_hr?: number | null;
  pfs_hr_95ci?: string | null;
  mos_exp_mos?: number | null;
  mos_control_mos?: number | null;
  os_hr?: number | null;
  os_hr_95ci?: string | null;
  g3or4_ae_rate_exp?: number | null;
  g3or4_ae_rate_control?: number | null;
  g3or4_teae_rate_exp?: number | null;
  g3or4_teae_rate_control?: number | null;
  g3or4_trae_rate_exp?: number | null;
  g3or4_trae_rate_control?: number | null;
  g3_5_ae_exp?: number | null;
  g3_5_ae_ctrl?: number | null;
  g3_5_teae_exp?: number | null;
  g3_5_teae_ctrl?: number | null;
  g5ordeath_exp?: number | null;
  g5ordeath_control?: number | null;
  drop_out_rate_exp?: number | null;
  drop_out_rate_ctrl?: number | null;
  pub_date?: string | Date | null;
  publication_date?: string | Date | null;
  exp_drug_array?: string[];
  ctrl_drug_array?: string[];
  phase?: string; // Phase information from ruby_data table
  /** Non-conventional/indication-specific efficacy (CML, MM, etc.); merged from backend when top-level is null */
  other_efficacy_dict?: Record<string, number | string | null> | null;
}

// Transform backend data to frontend Trial format
function transformTrial(backendTrial: BackendTrial): Trial {
  const normalizeText = (value: unknown): string => {
    if (value === null || value === undefined) return '';
    if (Array.isArray(value)) {
      const parts = value.map((v) => String(v).trim()).filter(Boolean);
      return parts.join(', ');
    }
    if (typeof value === 'object') {
      try {
        return JSON.stringify(value);
      } catch (e) {
        return String(value);
      }
    }
    return String(value).trim();
  };

  const pickText = (...values: unknown[]): string => {
    for (const value of values) {
      const normalized = normalizeText(value);
      if (normalized) return normalized;
    }
    return '';
  };

  const parseNumber = (value: unknown): number | null => {
    if (value === null || value === undefined || value === '') return null;
    const num = Number(value);
    return Number.isFinite(num) ? num : null;
  };

  /** Prefer top-level value, then other_efficacy_dict[key] for SOC indication-specific endpoints */
  const pickFromTrialOrDict = (topLevel: unknown, dictKey: string): number | null => {
    const n = parseNumber(topLevel);
    if (n !== null) return n;
    const oed = (backendTrial as { other_efficacy_dict?: Record<string, unknown> }).other_efficacy_dict;
    return parseNumber(oed?.[dictKey]) ?? null;
  };

  /** G3-5 AEs resolved in resolveG35Ae (ruby_data safety columns). */
  const g35 = resolveG35Ae(backendTrial);
  const trialName = backendTrial.trial_name || backendTrial.nct_id || '';

  /** Format ruby_data.exp_drug_array / ctrl_drug_array (Postgres text[] → JSON array). */
  const formatDrugArray = (value: unknown): string => {
    const isJunkToken = (token: string): boolean => {
      const t = token.trim();
      if (!t) return true;
      // Empty / brace-only artifacts from bad imports or pg array literals
      if (/^[{}\[\]()",\\]+$/.test(t)) return true;
      if (t === '{}' || t === '[]' || t === 'null' || t === 'undefined') return true;
      return false;
    };

    let parts: string[] = [];
    if (Array.isArray(value)) {
      parts = value.map((v) => String(v ?? '').trim()).filter((v) => !isJunkToken(v));
    } else if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed || isJunkToken(trimmed)) return '';
      // JSON array string
      if (trimmed.startsWith('[')) {
        try {
          const parsed = JSON.parse(trimmed);
          if (Array.isArray(parsed)) {
            parts = parsed.map((v) => String(v ?? '').trim()).filter((v) => !isJunkToken(v));
          }
        } catch {
          /* fall through */
        }
      }
      // Postgres array literal: {a,b,"c d"} or empty {}
      if (parts.length === 0 && trimmed.startsWith('{') && trimmed.endsWith('}')) {
        const inner = trimmed.slice(1, -1).trim();
        if (!inner) return '';
        parts = inner
          .split(',')
          .map((p) => p.trim().replace(/^"(.*)"$/, '$1').replace(/\\"/g, '"'))
          .filter((v) => !isJunkToken(v));
        // Do not fall back to raw "{...}" — that caused UI to show "{}" / brace junk.
        return parts.join(' + ');
      }
      // Plain non-array string (shouldn't happen for these columns)
      if (parts.length === 0 && !trimmed.startsWith('{') && !trimmed.startsWith('[')) {
        parts = isJunkToken(trimmed) ? [] : [trimmed];
      }
    }
    return parts.join(' + ');
  };

  // Regimen (exp / ctrl) — ONLY from exp_drug_array / ctrl_drug_array (no text-field fallbacks).
  const expDrugFromArray = formatDrugArray(
    backendTrial.exp_drug_array ?? (backendTrial as any).expDrugArray,
  );
  const ctrlDrugFromArray = formatDrugArray(
    backendTrial.ctrl_drug_array ?? (backendTrial as any).ctrlDrugArray,
  );
  const expDrug = expDrugFromArray || 'N/A';
  const ctrlDrug = ctrlDrugFromArray || 'N/A';
  const experimentalRegimen = expDrugFromArray;
  const controlRegimen = ctrlDrugFromArray;

  // Format publication date - handle different formats from pmid_info
  let publicationDate = 'N/A';
  const publicationDateValue = backendTrial.pub_date ?? backendTrial.publication_date;
  if (publicationDateValue) {
    try {
      if (typeof publicationDateValue === 'string') {
        // Handle different string formats
        const dateStr = publicationDateValue.trim();
        
        // If it's ISO with a time component, normalize to YYYY-MM-DD
        if (dateStr.match(/^\d{4}-\d{2}-\d{2}T/)) {
          const parsedDate = new Date(dateStr);
          if (!isNaN(parsedDate.getTime())) {
            publicationDate = parsedDate.toISOString().split('T')[0];
          } else {
            publicationDate = dateStr;
          }
        } else if (dateStr.match(/^\d{4}-\d{2}-\d{2}/)) {
          publicationDate = dateStr;
        } else {
          // Try to parse other formats
          const parsedDate = new Date(dateStr);
          if (!isNaN(parsedDate.getTime())) {
            // Convert to ISO format (YYYY-MM-DD)
            publicationDate = parsedDate.toISOString().split('T')[0];
          } else {
            // If can't parse, use the string as-is
            publicationDate = dateStr;
          }
        }
      } else if (publicationDateValue instanceof Date) {
        publicationDate = publicationDateValue.toISOString().split('T')[0];
      } else if (typeof publicationDateValue === 'object' && 'year' in publicationDateValue) {
        // Handle date-like objects (e.g., from PostgreSQL date type)
        const year = (publicationDateValue as any).year || (publicationDateValue as any).getFullYear?.();
        const month = ((publicationDateValue as any).month || (publicationDateValue as any).getMonth?.() || 0) + 1;
        const day = (publicationDateValue as any).day || (publicationDateValue as any).getDate?.() || 1;
        publicationDate = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      }
    } catch (e) {
      console.warn('Error formatting publication date:', e, publicationDateValue);
      publicationDate = 'N/A';
    }
  }

  const normalizePreferredStatus = (value: unknown): Trial['preferredStatus'] => {
    if (!value) return 'N/A';
    const raw = String(value).trim();
    if (!raw) return 'N/A';
    const normalized = raw.toLowerCase().replace(/[_-]+/g, ' ');
    if (normalized.includes('preferred')) return 'Preferred';
    if (normalized.includes('other') && normalized.includes('recommended')) return 'Other Recommended';
    if (normalized.includes('useful') && normalized.includes('circumstance')) {
      return 'Useful in Certain Circumstances';
    }
    if (normalized === 'n/a' || normalized === 'na' || normalized === 'none') return 'N/A';
    return 'N/A';
  };

  // Map preferred_status to NCCNStatus type
  const preferredStatus = normalizePreferredStatus(backendTrial.preferred_status);

  const pmidFromIdentifier = backendTrial.identifier
    ? String(backendTrial.identifier).match(/\d+/)?.[0]
    : undefined;
  const pmidValue = backendTrial.pmid ? String(backendTrial.pmid) : pmidFromIdentifier;

  return {
    id: backendTrial.identifier || backendTrial.id?.toString() || 'unknown',
    pmid: pmidValue,
    trialName: trialName.trim(),
    nctId: pickText(
      backendTrial.nct_id,
      (backendTrial as any).nct,
      (backendTrial as any).nct_number,
      (backendTrial as any).nctid
    ),
    doi: pickText(backendTrial.doi, (backendTrial as any).doi_link, (backendTrial as any).doi_url),
    trialArmNotes: (() => {
      const raw = pickText(
        backendTrial.trial_arm_notes,
        (backendTrial as any).trial_arm,
        (backendTrial as any).trial_arm_note,
        (backendTrial as any).trial_arm_notes_text
      );
      // Numeric-only notes (e.g. arm index "1") are not useful in the UI.
      if (/^\d+(\.\d+)?$/.test(raw.trim())) return '';
      return raw;
    })(),
    indicationDetails: pickText(
      backendTrial.indication_details,
      (backendTrial as any).indication
    ),
    primaryEndpoint: pickText(
      backendTrial.primary_endpoint,
      (backendTrial as any).primary_outcome_measures,
      (backendTrial as any).primary_outcome
    ),
    secondaryEndpoints: pickText(
      backendTrial.secondary_endpoints,
      (backendTrial as any).secondary_outcome_measures,
      (backendTrial as any).secondary_outcome
    ),
    medianFollowUpTimeMonths: backendTrial.median_follow_up_time_months
      ?? (backendTrial as any).median_follow_up_time
      ?? (backendTrial as any).median_follow_up,
    baselineCharacteristics: pickText(
      backendTrial.baseline_characteristics,
      (backendTrial as any).baseline_dict
    ),
    biomarkersInfo: pickText(backendTrial.biomarkers_info),
    experimentalRegimen,
    controlRegimen,
    otherEfficacy: pickText(
      backendTrial.other_efficacy,
      (backendTrial as any).other_efficacy_dict
    ),
    otherInfoAboutAEs: pickText(
      backendTrial.other_info_about_aes,
      (backendTrial as any).ae_dict
    ),
    nccnNotes: backendTrial.nccn_notes || '',
    preferredStatus: preferredStatus,
    expDrug: expDrug,
    ctrlDrug: ctrlDrug,
    expN: backendTrial.exp_n || 0,
    ctrlN: backendTrial.control_n || 0,
    orrExp: backendTrial.orr_exp ? Number(backendTrial.orr_exp) : null,
    orrCtrl: backendTrial.orr_control ? Number(backendTrial.orr_control) : null,
    crExp: backendTrial.cr_exp ? Number(backendTrial.cr_exp) : null,
    crCtrl: backendTrial.cr_control ? Number(backendTrial.cr_control) : null,
    mPFSExp: backendTrial.mpfs_exp_mos ? Number(backendTrial.mpfs_exp_mos) : null,
    mPFSCtrl: backendTrial.mpfs_control_mos ? Number(backendTrial.mpfs_control_mos) : null,
    pfsHR: backendTrial.pfs_hr ? Number(backendTrial.pfs_hr) : null,
    pfsHRCI: backendTrial.pfs_hr_95ci || '',
    mOSExp: backendTrial.mos_exp_mos ? Number(backendTrial.mos_exp_mos) : null,
    mOSCtrl: backendTrial.mos_control_mos ? Number(backendTrial.mos_control_mos) : null,
    osHR: backendTrial.os_hr ? Number(backendTrial.os_hr) : null,
    osHRCI: backendTrial.os_hr_95ci || '',
    g35AEExp: g35.exp,
    g35AECtrl: g35.ctrl,
    g35AESource: g35.source,
    g34AEExp: backendTrial.g3or4_ae_rate_exp ? Number(backendTrial.g3or4_ae_rate_exp) : null,
    g34AECtrl: backendTrial.g3or4_ae_rate_control ? Number(backendTrial.g3or4_ae_rate_control) : null,
    // Calculate g5 death percentage from count and sample size
    g5DeathExp: backendTrial.g5ordeath_exp ? Number(backendTrial.g5ordeath_exp) : null,
    g5DeathCtrl: backendTrial.g5ordeath_control ? Number(backendTrial.g5ordeath_control) : null,
    dropOutExp: backendTrial.drop_out_rate_exp ? Number(backendTrial.drop_out_rate_exp) : null,
    dropOutCtrl: backendTrial.drop_out_rate_ctrl ? Number(backendTrial.drop_out_rate_ctrl) : null,
    publicationDate: publicationDate,
    phase: pickText(
      backendTrial.phase,
      (backendTrial as any).phases,
      (backendTrial as any).trial_phase,
      (backendTrial as any).trial_phases
    ) || undefined, // Include phase if available
    // Indication-specific endpoints (SOC reports); use top-level first, then other_efficacy_dict
    criExp: pickFromTrialOrDict((backendTrial as any).cri_exp_, 'cri_exp_'),
    criCtrl: pickFromTrialOrDict((backendTrial as any).cri_cont_, 'cri_cont_'),
    hematologicalCrExp: pickFromTrialOrDict((backendTrial as any).hematological_cr_exp_, 'hematological_cr_exp_'),
    hematologicalCrCtrl: pickFromTrialOrDict((backendTrial as any).hematological_cr_control_, 'hematological_cr_control_'),
    cytogeneticCrExp: pickFromTrialOrDict((backendTrial as any).cytogenetic_cr_exp_, 'cytogenetic_cr_exp_'),
    cytogeneticCrCtrl: pickFromTrialOrDict((backendTrial as any).cytogenetic_cr_control_, 'cytogenetic_cr_control_'),
    cytogeneticMajorResponseExp: pickFromTrialOrDict((backendTrial as any).cytogenetic_major_response_exp_, 'cytogenetic_major_response_exp_'),
    cytogeneticMajorResponseCtrl: pickFromTrialOrDict((backendTrial as any).cytogenetic_major_response_control_, 'cytogenetic_major_response_control_'),
    molecularMajorResponseExp: pickFromTrialOrDict((backendTrial as any).molecular_major_response_exp_, 'molecular_major_response_exp_'),
    molecularMajorResponseCtrl: pickFromTrialOrDict((backendTrial as any).molecular_major_response_control_, 'molecular_major_response_control_'),
    scrExp: pickFromTrialOrDict((backendTrial as any).scr_exp_, 'scr_exp_'),
    scrCtrl: pickFromTrialOrDict((backendTrial as any).scr_control_, 'scr_control_'),
    vgprExp: pickFromTrialOrDict((backendTrial as any).vgpr_exp_, 'vgpr_exp_'),
    vgprCtrl: pickFromTrialOrDict((backendTrial as any).vgpr_control_, 'vgpr_control_'),
    psa50Exp: pickFromTrialOrDict((backendTrial as any).psa50_exp_, 'psa50_exp_'),
    psa50Ctrl: pickFromTrialOrDict((backendTrial as any).psa50_control_, 'psa50_control_'),
    nccnRegimen: pickText((backendTrial as any).nccn_regimen),
    mapIdentifier: pickText((backendTrial as any).map_identifier),
    refNum: pickText((backendTrial as any).ref_num),
    refContents: pickText((backendTrial as any).ref_contents),
  };
}

function normalizePreferredStatusFromBackend(value: unknown): NCCNStatus {
  if (!value) return 'Other Recommended';
  const raw = String(value).trim();
  if (!raw) return 'Other Recommended';
  const normalized = raw.toLowerCase().replace(/[_-]+/g, ' ');
  if (normalized.includes('preferred') && !normalized.includes('other')) return 'Preferred';
  if (normalized.includes('other') && normalized.includes('recommended')) return 'Other Recommended';
  if (normalized.includes('useful') && normalized.includes('circumstance')) return 'Useful in Certain Circumstances';
  // pmid_map often stores literal "None" for uncategorized NCCN regimens — keep them visible.
  if (normalized === 'n/a' || normalized === 'na' || normalized === 'none' || normalized === 'null') {
    return 'Other Recommended';
  }
  return 'Other Recommended';
}

function transformSocRegimenGroup(raw: Record<string, unknown>): SocRegimenGroup {
  const refsRaw = Array.isArray(raw.references) ? raw.references : [];
  const references: SocRegimenReference[] = refsRaw.map((ref: Record<string, unknown>) => {
    const trialsRaw = Array.isArray(ref.trials) ? ref.trials : ref.trial ? [ref.trial] : [];
    const trials = trialsRaw
      .filter((t): t is BackendTrial => t != null && typeof t === 'object')
      .map((t) => transformTrial(t));
    return {
      mapIdentifier: ref.map_identifier != null ? String(ref.map_identifier) : null,
      refNum: ref.ref_num != null ? String(ref.ref_num) : null,
      refContents: ref.ref_contents != null ? String(ref.ref_contents) : null,
      pmid: ref.pmid != null ? String(ref.pmid) : null,
      trials,
      trial: trials[0] ?? null,
    };
  });
  return {
    regimenName: String(raw.regimen_name || 'Unspecified regimen'),
    preferredStatus: normalizePreferredStatusFromBackend(raw.preferred_status),
    nccnNotes: String(raw.nccn_notes || ''),
    evidenceCategory: String(raw.evidence_category || '').trim() || undefined,
    pmids: Array.isArray(raw.pmids) ? raw.pmids.map(String) : [],
    referenceCount: typeof raw.reference_count === 'number' ? raw.reference_count : references.length,
    references,
  };
}

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true, // Important for Flask session cookies
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for adding auth tokens if needed
api.interceptors.request.use(
  (config) => {
    // Add any auth headers here if using token-based auth
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for handling errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Log network errors for debugging
    if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
      console.error('Network Error:', {
        message: error.message,
        code: error.code,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          baseURL: error.config?.baseURL,
        }
      });
    }
    
    // Log detailed error info for API calls
    if (error.response) {
      console.error('API Error Response:', {
        url: error.config?.url,
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data,
        headers: error.response.headers,
      });
    }
    
    if (error.response?.status === 401) {
      // Handle unauthorized - redirect to login
      if (error.config?.url?.includes('cancer-types') || 
          error.config?.url?.includes('get-indications') ||
          error.config?.url?.includes('fetch-trials') ||
          error.config?.url?.includes('generate-report')) {
        // Only redirect if we're not already on the login page
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

export interface FetchTrialsParams {
  indication_code?: string;
  nccn_type?: string;
  nccn_version?: string;
  limit?: number;
}

export interface FetchTrialsResult {
  trials: Trial[];
  regimenGroups: SocRegimenGroup[];
}

export interface ChatbotRequest {
  query: string;
  model?: string;
  conversation_history?: Array<{ role: 'user' | 'assistant'; content: string }>;
  response_id?: string;
  page_context?: string;
  /** Same as page_context; send both so backend always receives trial data (gemini-chatbot accepts either). */
  trial_data?: string;
  page_title?: string;
  page_url?: string;
}

export interface ChatbotResponse {
  reply: string;
  sources?: Array<{
    source_id?: string;
    title?: string;
    document?: string;
    section?: string;
    chunk_id?: string;
    url?: string;
    file?: string;
    rationale?: string;
    why?: string;
  }>;
  response_id?: string;
  conversation_history?: Array<{ role: 'user' | 'assistant'; content: string }>;
  records_found?: number;
  token_usage?: {
    prompt_tokens?: number;
    completion_tokens?: number;
    total_tokens?: number;
  };
}

export interface PrimeReport {
  id: number;
  title: string;
  description: string;
  token_cost: number;
  category?: string;
  created_at?: string;
  google_drive_link?: string;
  purchased_at?: string;
  purchased_by?: string;
}

export interface SuperuserPrimeReport extends PrimeReport {
  is_active: boolean;
  purchase_count: number;
  total_revenue: number;
}

export interface CancerType {
  id: string;
  name: string;
}

export interface Indication {
  id: string;
  name: string;
}

/** In-memory cache so parallel callers (e.g. StrictMode double mount) share one GET /api/cancer-types. */
let cancerTypesMemoryCache: CancerType[] | null = null;
let cancerTypesInflightRequest: Promise<CancerType[]> | null = null;

/** Session-lifetime caches for Asset Radar's slow (0.5-5s) per-indication/per-trial endpoints -- see the
 * arGetWorkbook/arGetTimeline/arGetTrial comment below for why these exist. */
const arWorkbookCache = new Map<string, any>();
const arWorkbookInflight = new Map<string, Promise<any>>();
const arTimelineCache = new Map<string, any>();
const arTimelineInflight = new Map<string, Promise<any>>();
const arTrialCache = new Map<string, any>();
const arTrialInflight = new Map<string, Promise<any>>();
let arIndexAllCache: ArIndexAllEntry[] | null = null;
let arIndexAllInflight: Promise<ArIndexAllEntry[]> | null = null;

/** Defensive normalization shared by arGetWorkbook/arGetIndexAll: a real synced workbook can have an
 * indication with a landscape but no assets parsed, or a calendar tab that failed to parse, which
 * otherwise crashes every downstream consumer (.map/.filter/for-of) expecting an array. Backend shapes
 * (asset_radar/api.py) are a bare array for `cal` and an object with an `assets` array for `land`. */
function normalizeArWorkbook(data: any): any {
  if (data && Array.isArray(data.inds)) {
    for (const ind of data.inds) {
      if (ind.land && !Array.isArray(ind.land.assets)) ind.land.assets = [];
      if (ind.cal && !Array.isArray(ind.cal)) ind.cal = [];
    }
  }
  return data;
}

export interface GenerateReportRequest {
  indication_code: string;
  nccn_type: string;
}

export interface GenerateReportResponse {
  url?: string;
  indication_name?: string;
  message?: string;
  error?: string;
  rate_limited?: boolean;
  reset_time?: string;
  current_count?: number;
  limit?: number;
}

export const apiService = {
  // Trial data
  async fetchTrials(params: FetchTrialsParams = {}): Promise<FetchTrialsResult> {
    const response = await api.get('/fetch-trials', { params });
    const data = response.data;
    if (data?.error) {
      throw new Error(data.error);
    }
    // Legacy: plain array (older backends)
    if (Array.isArray(data)) {
      return { trials: data.map(transformTrial), regimenGroups: [] };
    }
    const trials = Array.isArray(data?.trials) ? data.trials.map((t: BackendTrial) => transformTrial(t)) : [];
    const regimenGroups = Array.isArray(data?.regimen_groups)
      ? data.regimen_groups.map((g: Record<string, unknown>) => transformSocRegimenGroup(g))
      : [];
    return { trials, regimenGroups };
  },

  async fetchSocRegimenGroups(params: FetchTrialsParams = {}): Promise<SocRegimenGroup[]> {
    const response = await api.get('/fetch-soc-regimen-groups', { params });
    if (Array.isArray(response.data)) {
      return response.data.map((row) => transformSocRegimenGroup(row as Record<string, unknown>));
    }
    if (response.data?.error) {
      throw new Error(response.data.error);
    }
    return [];
  },

  // Indication info
  async fetchIndicationInfo(params: { indication_code: string; nccn_type?: string }): Promise<IndicationInfo> {
    const response = await api.get('/fetch-ind-info', { params });
    const rawLinks = response.data.manual_emerging_links;
    const manualEmergingLinks = Array.isArray(rawLinks)
      ? rawLinks
          .map(
            (row: { emerging_indication_id?: number; priority?: number; note?: string }) => ({
              emergingIndicationId: String(row.emerging_indication_id ?? ''),
              priority: typeof row.priority === 'number' ? row.priority : 100,
              note: typeof row.note === 'string' ? row.note : undefined,
            }),
          )
          .filter((l) => l.emergingIndicationId.length > 0)
      : undefined;
    return {
      name: response.data.indication || '',
      nccnVersion: response.data.nccn_version || '',
      code: params.indication_code,
      cancerType: response.data.cancer_type || '',
      ...(manualEmergingLinks && manualEmergingLinks.length > 0 ? { manualEmergingLinks } : {}),
    };
  },

  // Cancer types for report ordering (deduped: React StrictMode / multiple mounts share one request)
  async fetchCancerTypes(): Promise<CancerType[]> {
    if (cancerTypesMemoryCache) {
      return cancerTypesMemoryCache;
    }
    if (cancerTypesInflightRequest) {
      return cancerTypesInflightRequest;
    }
    cancerTypesInflightRequest = (async () => {
      try {
        const response = await api.get('/api/cancer-types');
        let list: CancerType[] = [];
        if (Array.isArray(response.data)) {
          list = response.data;
        } else if (response.data && Array.isArray(response.data.cancer_types)) {
          list = response.data.cancer_types;
        } else {
          console.error('Unexpected cancer types response format:', response.data);
        }
        if (list.length > 0) {
          cancerTypesMemoryCache = list;
        }
        return list;
      } catch (error: any) {
        console.error('Error fetching cancer types:', error);
        if (error.response?.status === 401 || error.response?.status === 403) {
          console.warn('Authentication required for cancer types. User may need to log in.');
        }
        return [];
      } finally {
        cancerTypesInflightRequest = null;
      }
    })();
    return cancerTypesInflightRequest;
  },

  // Indications for a cancer type
  async fetchIndications(cancerType: string): Promise<Indication[]> {
    try {
      const response = await api.get(`/get-indications/${cancerType}`);
      // Ensure we return an array
      if (Array.isArray(response.data)) {
        return response.data;
      } else {
        console.error('Unexpected indications response format:', response.data);
        return [];
      }
    } catch (error: any) {
      console.error('Error fetching indications:', error);
      return [];
    }
  },

  // Generate report
  async generateReport(request: GenerateReportRequest): Promise<GenerateReportResponse> {
    const response = await api.post('/generate-report', request);
    return response.data;
  },

  // Fetch recent indications
  async fetchRecentIndications(): Promise<RecentIndication[]> {
    try {
      const response = await api.get('/api/recent-indications');
      return Array.isArray(response.data) ? response.data : [];
    } catch (error: any) {
      console.error('Error fetching recent indications:', error);
      return [];
    }
  },

  async fetchClinicalDashboardState(): Promise<ClinicalDashboardState | null> {
    try {
      const response = await api.get('/api/user/clinical-dashboard-state');
      const d = response.data;
      if (d == null || typeof d !== 'object' || !(d as ClinicalDashboardState).indication_code) {
        return null;
      }
      return d as ClinicalDashboardState;
    } catch (error: any) {
      console.error('Error fetching clinical dashboard state:', error);
      return null;
    }
  },

  async saveClinicalDashboardState(payload: {
    cancer_type_id: string;
    indication_code: string;
    nccn_type: string;
    active_emerging_id?: string | null;
  }): Promise<{ success: boolean; error?: string }> {
    const response = await api.put('/api/user/clinical-dashboard-state', payload);
    return response.data;
  },

  async clearClinicalDashboardState(): Promise<{ success: boolean; error?: string }> {
    const response = await api.delete('/api/user/clinical-dashboard-state');
    return response.data;
  },

  async submitAcademicQuestionnaire(payload: Record<string, any>): Promise<{ success: boolean; error?: string }> {
    const response = await api.post('/api/academic-questionnaire', payload);
    return response.data;
  },

  // Emerging data endpoints
  async fetchEmergingIndications(): Promise<EmergingIndication[]> {
    if (_emergingIndicationsCache) return _emergingIndicationsCache;
    if (_emergingIndicationsFetch) return _emergingIndicationsFetch;
    _emergingIndicationsFetch = (async () => {
      try {
        const response = await api.get('/api/emerging-indications');
        const data = Array.isArray(response.data) ? response.data : [];
        _emergingIndicationsCache = data;
        return data;
      } catch (error: any) {
        console.error('Error fetching emerging indications:', error);
        _emergingIndicationsFetch = null;
        return [];
      }
    })();
    return _emergingIndicationsFetch;
  },

  async fetchEmergingRecommendations(indicationName: string): Promise<EmergingIndication[]> {
    try {
      const response = await api.get('/api/emerging-recommendations', {
        params: { indication_name: indicationName }
      });
      return Array.isArray(response.data) ? response.data : [];
    } catch (error: any) {
      console.error('Error fetching emerging recommendations:', error);
      return [];
    }
  },

  async addEmergingIndication(
    indication_name: string,
    cancer_type?: string | null,
  ): Promise<{ success: boolean; id?: string; name?: string; cancer_type?: string | null; error?: string }> {
    const response = await api.post('/superuser/emerging-indications/add', {
      indication_name,
      ...(cancer_type != null && String(cancer_type).trim()
        ? { cancer_type: String(cancer_type).trim() }
        : {}),
    });
    // New indication must show up in subsequent list fetches.
    _emergingIndicationsCache = null;
    _emergingIndicationsFetch = null;
    return response.data;
  },

  async patchEmergingIndicationClientDirectory(
    indicationId: string,
    body: Partial<{
      show_in_client_emerging_directory: boolean;
      show_when_no_published_trials: boolean;
      client_efficacy_layout: 'split' | 'combined';
    }>
  ): Promise<{ success: boolean; error?: string }> {
    const response = await api.patch(`/api/superuser/emerging-indications/${indicationId}/client-directory`, body);
    return response.data;
  },

  async fetchRecentEmergingIndications(): Promise<RecentEmergingIndication[]> {
    try {
      const response = await api.get('/api/recent-emerging-indications');
      return Array.isArray(response.data) ? response.data : [];
    } catch (error: any) {
      console.error('Error fetching recent emerging indications:', error);
      return [];
    }
  },

  async fetchEmergingData(indicationSlug: string, options?: { limit?: number }): Promise<{
    success: boolean;
    data: EmergingDataRow[];
    source_rows_by_trial?: Record<string, EmergingSourceRow[]>;
    emerging_tx_audit_by_trial?: Record<string, EmergingTxAuditEntry[]>;
    indication_name: string;
    last_scraped_at?: string | null;
    last_viewed_at?: string | null;
    previous_last_viewed_at?: string | null;
    is_superuser: boolean;
    can_edit_emerging?: boolean;
    error?: string;
  }> {
    // URL encode the indication slug to handle spaces and special characters
    const encodedSlug = encodeURIComponent(indicationSlug);
    const limit = typeof options?.limit === 'number' && options.limit > 0 ? options.limit : 200;
    const response = await api.get(`/api/react-emerging-data/${encodedSlug}`, { params: { limit } });
    return response.data;
  },

  async fetchLastEmergingScrape(): Promise<EmergingScrapeSummary | null> {
    const response = await api.get('/api/emerging-scrape/last-run');
    return response.data?.last_run || null;
  },

  async fetchRecentEmergingScrapes(limit = 10): Promise<EmergingScrapeSummary[]> {
    const response = await api.get('/api/emerging-scrape/recent', { params: { limit } });
    return Array.isArray(response.data?.recent) ? response.data.recent : [];
  },

  async getEmergingEditUsers(): Promise<{ id: number; email: string; role: string; can_edit_emerging: boolean }[]> {
    const response = await api.get('/api/superuser/emerging-edit-users');
    return Array.isArray(response.data?.users) ? response.data.users : [];
  },

  async setEmergingEditUser(userId: number, canEditEmerging: boolean): Promise<{ success: boolean; can_edit_emerging: boolean }> {
    const response = await api.post(`/api/superuser/emerging-edit-users/${userId}`, { can_edit_emerging: canEditEmerging });
    return response.data;
  },

  async setEmergingEditUserByEmail(email: string, canEditEmerging: boolean): Promise<{ success: boolean; user_id: number; email: string; can_edit_emerging: boolean }> {
    const response = await api.post('/api/superuser/emerging-edit-users/by-email', { email, can_edit_emerging: canEditEmerging });
    return response.data;
  },

  async triggerEmergingScrape(payload: {
    mode?: 'emerging' | 'catalysts' | 'both';
    run_emerging?: boolean;
    run_catalysts?: boolean;
    limit?: number;
    userInstructions?: string;
    use_cached_data?: boolean;
    enrich_trials?: boolean;
    enrich_all_phases?: boolean;
    max_rounds?: number;
    max_sources?: number;
    max_assets?: number;
    enrich_delay_seconds?: number;
    fetch_existing_assets?: boolean;
    discover_studies?: boolean;
    save_new_assets?: boolean;
    /** Emerging report / indication names for the scraper */
    indications?: string[] | string;
    indication_code?: string;
    /** emerging_disease_indications.id values */
    indication_codes?: Array<string | number> | string;
    asset_ids?: number[] | string;
    /** Merged server-side with irrelevant-trial NCTs */
    excluded_nct_ids?: string[];
    excluded_source_urls?: string[];
    /** Merged server-side: skip data enrichment for these NCTs (includes irrelevant trials) */
    enrichment_excluded_nct_ids?: string[];
  } = {}): Promise<{ success?: boolean; status_code?: number; response?: any; error?: string; message?: string }> {
    const response = await api.post('/api/emerging-scrape/trigger', payload);
    return response.data;
  },

  async updateEmergingEntry(payload: {
    id: number;
    table?: string;
    data_type?: 'emerging' | 'soc';
    drug_name?: string;
    drug_names?: string[];
    asset_status?: string | null;
    overall_status?: string | null;
    no_data_yet?: boolean;
    regimen_details?: Record<string, any>;
    efficacy_data?: Record<string, any>;
    safety_data?: Record<string, any>;
    population_details?: Record<string, any>;
    source?: string[] | string;
    source_name?: string[] | string;
    data_date?: string | null;
    company?: string;
    eal_id?: number;
    oet_id?: number;
    oet_updates?: Record<string, any>;
  }): Promise<any> {
    const response = await api.post('/superuser/emerging-edit', {
      table: 'emerging_trials',
      ...payload,
    });
    return response.data;
  },

  /** Apply many trial edits in one DB transaction (see /superuser/emerging-edit-batch). */
  async updateEmergingEntryBatch(entries: Record<string, unknown>[]): Promise<{
    success?: boolean;
    updated?: number;
    error?: string;
  }> {
    const response = await api.post('/superuser/emerging-edit-batch', { entries });
    return response.data;
  },

  /** Scrape ClinicalTrials.gov for the given NCT. preview=true returns data only; preview=false updates DB. */
  async scrapeEmergingTrialNct(payload: {
    trial_id: number;
    nct_id: string;
    preview?: boolean;
  }): Promise<any> {
    const response = await api.post('/api/superuser/emerging-trial-scrape-nct', payload);
    return response.data;
  },

  /** Create a 1emerging_trials row from ClinicalTrials.gov; links trial to indication_codes (or merges if NCT exists). */
  async addEmergingTrialByNct(payload: {
    indication_code: string;
    nct_id: string;
  }): Promise<{
    success?: boolean;
    error?: string;
    trial_id?: number;
    nct_id?: string;
    message?: string;
    merged?: boolean;
    already_linked?: boolean;
  }> {
    const response = await api.post('/api/superuser/emerging-trial-add-by-nct', payload);
    return response.data;
  },

  async addEmergingEntry(payload: {
    table?: string;
    indication_code: string;
    indication?: string;
    manual_entry?: boolean;
    data_type?: 'emerging' | 'soc';
    study_name?: string;
    nct_id?: string;
    phases?: string;
    drug_name?: string;
    company?: string;
    trial_details?: Record<string, any>;
    regimen_details?: Record<string, any>;
    population_details?: Record<string, any>;
    efficacy_data?: Record<string, any>;
    safety_data?: Record<string, any>;
    source?: string[] | string;
    source_name?: string[] | string;
    data_date?: string | null;
    internal_reviewed?: boolean;
  }): Promise<any> {
    const response = await api.post('/superuser/emerging-add', {
      table: 'emerging_trials',
      ...payload,
    });
    return response.data;
  },

  async publishEmergingEntry(payload: { id: number; mark: boolean; indication_code?: number }): Promise<any> {
    const response = await api.post('/superuser/emerging-publish', {
      table: 'emerging_trials',
      ...payload,
    });
    return response.data;
  },

  async deleteEmergingEntry(payload: { id: number }): Promise<any> {
    const response = await api.post('/superuser/emerging-delete', {
      table: 'emerging_trials',
      ...payload,
    });
    return response.data;
  },

  async searchSocEntries(query: string, indicationSlug: string): Promise<any> {
    const response = await api.get('/superuser/search-soc', {
      params: {
        q: query,
        indication_slug: indicationSlug,
      },
    });
    return response.data;
  },

  async featureSocEntry(payload: { indication_slug: string; ruby_data_id: number; display_order?: number }): Promise<any> {
    const response = await api.post('/superuser/feature-soc', payload);
    return response.data;
  },

  async orderEmergingReport(indicationCode: string, indicationName: string): Promise<any> {
    const response = await api.post('/order-emerging-report', { 
      indication_code: indicationCode,
      indication_name: indicationName
    });
    return response.data;
  },

  async fetchEmergingAssetsList() {
    const response = await api.get('/api/emerging-assets');
    return response.data;
  },

  async fetchEmergingAssetTrials(assetId: number) {
    const response = await api.get(`/api/emerging-asset/${assetId}/trials`);
    return response.data;
  },

  async fetchEmergingAssetReports(assetId: number) {
    const response = await api.get(`/api/emerging-asset/${assetId}/reports`);
    return response.data;
  },

  async joinWaitlist(payload: {
    firstName: string;
    lastName: string;
    email: string;
    organization: string;
    role: string;
  }) {
    const response = await api.post('/api/waitlist', payload);
    return response.data;
  },

  // FDA approvals
  async fetchFDAApprovals(limit?: number) {
    const response = await api.get('/api/fda-approvals', limit ? { params: { limit } } : undefined);
    return response.data;
  },

  /** Single-call SoC + FDA + NCT/emerging merge with NCT crosswalk (see unified_platform_merge.py). */
  async postUnifiedPlatformMerge(payload: {
    indication_code: string;
    nccn_type: string;
    nccn_version: string;
    text_filter?: string;
    emerging_indication_ids?: number[];
    limits?: { soc?: number; fda?: number; nct_emerging?: number };
  }): Promise<UnifiedPlatformMergeResponse> {
    const response = await api.post('/api/unified-platform-merge', payload);
    return response.data;
  },

  async fetchClinicalPublications() {
    const response = await api.get('/api/clinical-publications');
    return response.data;
  },

  async fetchConferenceAbstracts() {
    const response = await api.get('/api/conference-abstracts');
    return response.data;
  },

  async fetchAug2026ReadoutTracker() {
    const response = await api.get('/api/aug2026-readout-tracker', { timeout: 45000 });
    return response.data;
  },

  async fetchAug2026ReadoutUpdates(limit = 250) {
    const response = await api.get('/api/aug2026-readout-tracker/updates', { params: { limit }, timeout: 45000 });
    return response.data;
  },

  async fetchAug2026ReadoutCompanySources(params: { ticker?: string; company_id?: string; limit?: number }) {
    const response = await api.get('/api/aug2026-readout-tracker/company-sources', {
      params,
      timeout: 20000,
    });
    return response.data;
  },

  async fetchAug2026ReadoutTimeline(trialId: string) {
    const response = await api.get(
      `/api/aug2026-readout-tracker/${encodeURIComponent(trialId)}/timeline`,
    );
    return response.data;
  },

  async fetchAug2026ReadoutTrial(trialId: string) {
    const response = await api.get(
      `/api/aug2026-readout-tracker/trials/${encodeURIComponent(trialId)}`,
    );
    return response.data;
  },

  async fetchAug2026ReadoutFlags() {
    const response = await api.get('/api/aug2026-readout-tracker/flags');
    return response.data;
  },

  async flagAug2026ReadoutTrial(trialId: string) {
    const response = await api.post(
      `/api/aug2026-readout-tracker/flags/${encodeURIComponent(trialId)}`,
    );
    return response.data;
  },

  async unflagAug2026ReadoutTrial(trialId: string) {
    const response = await api.delete(
      `/api/aug2026-readout-tracker/flags/${encodeURIComponent(trialId)}`,
    );
    return response.data;
  },

  async resolveAug2026ReadoutCompanyFlag(flagId: number, payload: {
    company_id?: string;
    ignore?: boolean;
    create_company?: { company_id: string; name: string; ticker?: string };
  }) {
    const response = await api.post(
      `/api/aug2026-readout-tracker/company-flags/${flagId}`,
      payload,
    );
    return response.data;
  },

  async updateAug2026ReadoutWatchlist(trialId: string, payload: Record<string, unknown>) {
    const response = await api.patch(`/api/aug2026-readout-tracker/watchlist/${encodeURIComponent(trialId)}`, payload);
    return response.data;
  },

  async updateAug2026ReadoutTimelineEntry(entryId: number, payload: Record<string, unknown>) {
    const response = await api.patch(`/api/aug2026-readout-tracker/timeline/${entryId}`, payload);
    return response.data;
  },

  async deleteAug2026ReadoutTimelineEntry(entryId: number) {
    const response = await api.delete(`/api/aug2026-readout-tracker/timeline/${entryId}`);
    return response.data;
  },

  async createAug2026ReadoutTimelineEntry(payload: Record<string, unknown>) {
    const response = await api.post('/api/aug2026-readout-tracker/timeline', payload);
    return response.data;
  },

  async fetchTrialTrackerFeed(opts?: {
    perIndication?: number;
    trialPerIndication?: number;
    indicationId?: string | number;
    indicationIds?: Array<string | number>;
  }) {
    const params: Record<string, string | number> = {};
    if (opts?.perIndication != null) params.per_indication = opts.perIndication;
    if (opts?.trialPerIndication != null) params.trial_per_indication = opts.trialPerIndication;
    const ids = [
      ...(opts?.indicationId != null && opts.indicationId !== 'all' ? [opts.indicationId] : []),
      ...(opts?.indicationIds || []).filter((id) => id != null && id !== 'all'),
    ].map(String);
    const unique = [...new Set(ids)];
    if (unique.length === 1) params.indication_id = unique[0];
    else if (unique.length > 1) params.indication_ids = unique.join(',');
    const response = await api.get('/api/trial-tracker/feed', { params });
    return response.data;
  },

  async fetchTrialTrackerTimeline() {
    const response = await api.get('/api/trial-tracker/timeline');
    return response.data;
  },

  async fetchTrialTrackerFollows() {
    const response = await api.get('/api/trial-tracker/follows');
    return response.data;
  },

  async addTrialTrackerFollow(followType: string, followValue: string) {
    const response = await api.post('/api/trial-tracker/follows', { follow_type: followType, follow_value: followValue });
    return response.data;
  },

  async removeTrialTrackerFollow(followId: number) {
    const response = await api.delete(`/api/trial-tracker/follows/${followId}`);
    return response.data;
  },

  async fetchTrialTrackerSuggestions(params?: { status?: string; type?: string; limit?: number }) {
    const response = await api.get('/api/superuser/trial-tracker-suggestions', { params });
    return response.data;
  },

  async approveTrialTrackerSuggestion(suggestionId: number, notes?: string) {
    const response = await api.post(`/api/superuser/trial-tracker-suggestions/${suggestionId}/approve`, { notes });
    return response.data;
  },

  async rejectTrialTrackerSuggestion(suggestionId: number, notes?: string) {
    const response = await api.post(`/api/superuser/trial-tracker-suggestions/${suggestionId}/reject`, { notes });
    return response.data;
  },

  async fetchTrialTrackerFavorites() {
    const response = await api.get('/api/trial-tracker/favorites');
    return response.data;
  },

  async toggleTrialTrackerFavorite(nctId: string) {
    const response = await api.post('/api/trial-tracker/favorites', { nct_id: nctId });
    return response.data;
  },

  async fetchTrialTrackerSavedViews() {
    const response = await api.get('/api/trial-tracker/saved-views');
    return response.data;
  },

  async saveTrialTrackerView(payload: {
    name: string;
    cat_filter?: string;
    phase_filter?: string;
    source_filter?: string;
    followed_snapshot?: unknown[];
    alerts_off_snapshot?: Record<string, boolean>;
  }) {
    const response = await api.post('/api/trial-tracker/saved-views', payload);
    return response.data;
  },

  async renameTrialTrackerSavedView(viewId: number, name: string) {
    const response = await api.patch(`/api/trial-tracker/saved-views/${viewId}`, { name });
    return response.data;
  },

  async deleteTrialTrackerSavedView(viewId: number) {
    const response = await api.delete(`/api/trial-tracker/saved-views/${viewId}`);
    return response.data;
  },

  async markTrialTrackerAlertRead(eventId: number) {
    const response = await api.post('/api/trial-tracker/alerts/read', { event_id: eventId });
    return response.data;
  },

  async markAllTrialTrackerAlertsRead() {
    const response = await api.post('/api/trial-tracker/alerts/read-all');
    return response.data;
  },

  async getTrialTrackerSettings() {
    const response = await api.get('/api/trial-tracker/settings');
    return response.data;
  },

  async updateTrialTrackerSettings(payload: { alert_types_off: Record<string, boolean>; delivery_mode: 'Instant' | 'Daily' | 'Weekly' }) {
    const response = await api.put('/api/trial-tracker/settings', payload);
    return response.data;
  },

  async fetchTrialTrackerIndications() {
    const response = await api.get('/api/trial-tracker/indications');
    return response.data;
  },

  async searchTrialTracker(
    q: string,
    opts?: {
      mode?: 'all' | 'asset' | 'indication' | 'nct';
      limit?: number;
      includeCtgov?: boolean;
      signal?: AbortSignal;
    },
  ) {
    const response = await api.get('/api/trial-tracker/search', {
      params: {
        q,
        mode: opts?.mode ?? 'all',
        limit: opts?.limit ?? 100,
        include_ctgov: opts?.includeCtgov === false ? '0' : '1',
      },
      timeout: 18000,
      signal: opts?.signal,
    });
    return response.data;
  },

  async fetchTrialTrackerFollowedTrials(limit = 200) {
    const response = await api.get('/api/trial-tracker/followed-trials', { params: { limit } });
    return response.data;
  },

  async searchTrialTrackerIndication(q: string, limit = 25) {
    const response = await api.get('/api/trial-tracker/indication-search', { params: { q, limit } });
    return response.data;
  },

  async trackTrialTrackerIndicationResults(query: string, nctIds: string[], indicationId?: number) {
    const response = await api.post('/api/trial-tracker/indication-search/track', {
      query, nct_ids: nctIds, indication_id: indicationId,
    });
    return response.data;
  },

  async testRunTrialTrackerRefresh(params?: { indicationId?: number; limit?: number }) {
    const response = await api.post('/api/trial-tracker/refresh/test-run', null, {
      params: { indication_id: params?.indicationId, limit: params?.limit },
    });
    return response.data;
  },

  async fetchTrialTrackerTrial(nctId: string) {
    const response = await api.get(`/api/trial-tracker/trials/${encodeURIComponent(nctId)}`);
    return response.data;
  },

  async fetchTrialTrackerTrialUpdates(nctId: string) {
    const response = await api.get(`/api/trial-tracker/trials/${encodeURIComponent(nctId)}/updates`);
    return response.data;
  },

  async testRunTrialTrackerDiscover(params?: { indicationId?: number; maxPerTerm?: number }) {
    const response = await api.post('/api/trial-tracker/discover/test-run', null, {
      params: { indication_id: params?.indicationId, max_per_term: params?.maxPerTerm },
    });
    return response.data;
  },

  async fetchAssets(params?: { modality?: string; target?: string; q?: string; company_id?: number; limit?: number }) {
    const response = await api.get('/api/assets', {
      params: {
        ...params,
        include_options: '1',
      },
    });
    return response.data;
  },

  async fetchAsset(assetId: number) {
    const response = await api.get(`/api/assets/${assetId}`);
    return response.data;
  },

  async fetchAssetsOptions() {
    const response = await api.get('/api/assets/options');
    return response.data;
  },

  async fetchAssetTrackerDetail(assetId: number) {
    const response = await api.get(`/api/assets/${assetId}/tracker-detail`);
    return response.data;
  },

  async fetchEmergingLandscapes(params?: { includeAll?: boolean }) {
    const response = await api.get('/api/emerging-landscapes', {
      params: params?.includeAll ? { include_all: '1' } : undefined,
    });
    return response.data;
  },

  async fetchEmergingLandscape(slug: string) {
    const response = await api.get(`/api/emerging-landscapes/${encodeURIComponent(slug)}`);
    return response.data;
  },

  async uploadEmergingLandscape(payload: {
    landscapeCsv: File;
    legendCsv?: File;
    slug: string;
    disease_area?: string;
    line_of_therapy?: string;
    version_label?: string;
    indication_name?: string;
    link_trials?: boolean;
    enrich_ncts?: boolean;
  }) {
    const form = new FormData();
    form.append('landscape_csv', payload.landscapeCsv);
    if (payload.legendCsv) form.append('legend_csv', payload.legendCsv);
    form.append('slug', payload.slug);
    if (payload.disease_area) form.append('disease_area', payload.disease_area);
    if (payload.line_of_therapy) form.append('line_of_therapy', payload.line_of_therapy);
    if (payload.version_label) form.append('version_label', payload.version_label);
    if (payload.indication_name) form.append('indication_name', payload.indication_name);
    form.append('link_trials', payload.link_trials === false ? 'false' : 'true');
    form.append('enrich_ncts', payload.enrich_ncts === false ? 'false' : 'true');
    const response = await api.post('/api/superuser/emerging-landscapes/upload', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  async setEmergingLandscapeVisibility(landscapeId: number, action: 'archive' | 'restore') {
    const response = await api.post(
      `/api/superuser/emerging-landscapes/${landscapeId}/visibility`,
      { action },
    );
    return response.data;
  },

  async fetchEmergingCalendars(params?: { includeAll?: boolean }) {
    const response = await api.get('/api/emerging-calendars', {
      params: params?.includeAll ? { include_all: true } : undefined,
    });
    return response.data;
  },

  async fetchEmergingCalendar(slug: string, params?: { versionNumber?: number }) {
    const response = await api.get(`/api/emerging-calendars/${encodeURIComponent(slug)}`, {
      params: params?.versionNumber != null ? { version_number: params.versionNumber } : undefined,
    });
    return response.data;
  },

  async uploadEmergingCalendar(payload: {
    calendarCsv: File;
    slug: string;
    disease_area?: string;
    line_of_therapy?: string;
    version_label?: string;
    indication_name?: string;
    link_trials?: boolean;
  }) {
    const form = new FormData();
    form.append('calendar_csv', payload.calendarCsv);
    form.append('slug', payload.slug);
    if (payload.disease_area) form.append('disease_area', payload.disease_area);
    if (payload.line_of_therapy) form.append('line_of_therapy', payload.line_of_therapy);
    if (payload.version_label) form.append('version_label', payload.version_label);
    if (payload.indication_name) form.append('indication_name', payload.indication_name);
    form.append('link_trials', payload.link_trials === false ? 'false' : 'true');
    const response = await api.post('/api/superuser/emerging-calendars/upload', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  async setEmergingCalendarVisibility(calendarId: number, action: 'archive' | 'restore') {
    const response = await api.post(
      `/api/superuser/emerging-calendars/${calendarId}/visibility`,
      { action },
    );
    return response.data;
  },

  async fetchSuperuserEmergingAssets(indicationCode?: string | number) {
    const params = indicationCode != null ? { indication_code: String(indicationCode) } : {};
    const response = await api.get('/api/superuser/emerging-assets', { params });
    return response.data;
  },

  async fetchSuperuserEmergingAssetsPicker() {
    const response = await api.get('/api/superuser/emerging-assets/picker');
    return response.data;
  },

  async splitEmergingAsset(payload: { asset_id: number; asset_names: string[] }) {
    const response = await api.post('/api/superuser/emerging-assets/split', payload);
    return response.data;
  },

  async renameEmergingAsset(payload: { asset_id: number; asset_name: string }) {
    const response = await api.post('/api/superuser/emerging-assets/rename', payload);
    return response.data;
  },

  async deleteEmergingAsset(payload: { asset_id: number }) {
    const response = await api.post('/api/superuser/emerging-assets/delete', payload);
    return response.data;
  },

  async unlinkEmergingAsset(payload: { asset_id: number; indication_code?: string | number }) {
    const response = await api.post('/api/superuser/emerging-assets/unlink', payload);
    return response.data;
  },

  async linkEmergingAsset(payload: {
    asset_id?: number;
    asset_name?: string;
    company_name?: string;
    indication_code: number;
    lifecycle_status?: string;
  }) {
    const response = await api.post('/api/superuser/emerging-assets/link', payload);
    return response.data;
  },

  async mergeEmergingAssets(payload:
    | { source_asset_id: number; target_asset_id: number }
    | { source_asset_ids: number[]; target_asset_id: number }
  ) {
    const response = await api.post('/api/superuser/emerging-assets/merge', payload);
    return response.data;
  },

  async fetchLandscapePipelineLandscapes(): Promise<{
    success: boolean;
    landscapes?: any[];
    running?: boolean;
    project?: string;
    region?: string;
    catalog?: any[];
    error?: string;
  }> {
    const response = await api.get('/api/superuser/landscape-pipeline/landscapes');
    return response.data;
  },

  async fetchLandscapePipelineRuns(limit = 20): Promise<{
    success: boolean;
    runs?: any[];
    running?: boolean;
    error?: string;
  }> {
    const response = await api.get('/api/superuser/landscape-pipeline/runs', { params: { limit } });
    return response.data;
  },

  async runLandscapePipeline(payload: {
    action: 'scout' | 'enrich' | 'format_qa' | 'source_qa' | 'promote' | 'import' | 'refresh';
    landscape_keys: string[];
  }): Promise<{
    success: boolean;
    message?: string;
    executions?: any[];
    error?: string;
    detail?: string;
  }> {
    const response = await api.post('/api/superuser/landscape-pipeline/run', payload);
    return response.data;
  },

  async fetchSuperuserEmergingIndications() {
    const response = await api.get('/api/superuser/emerging-indications');
    return response.data;
  },

  async renameEmergingIndication(payload: {
    indication_id: number;
    indication_name: string;
    cancer_type?: string | null;
  }) {
    const response = await api.post('/api/superuser/emerging-indications/rename', payload);
    return response.data;
  },

  async mergeEmergingIndications(payload:
    | { source_indication_id: number; target_indication_id: number }
    | { source_indication_ids: number[]; target_indication_id: number }
  ) {
    const response = await api.post('/api/superuser/emerging-indications/merge', payload);
    return response.data;
  },

  async setEmergingAssetStatus(_payload: { asset_id: number; indication_code: string | number; status: string | null }) {
    return { success: false, error: 'Per-indication lifecycle status is no longer stored.' };
  },

  async fetchPrimeReports() {
    const response = await api.get('/api/prime-reports');
    return response.data;
  },

  async fetchPrimeReportDetail(reportId: number) {
    const response = await api.get(`/api/prime-reports/${reportId}`);
    return response.data;
  },

  async purchasePrimeReport(reportId: number) {
    const response = await api.post('/purchase-prime-report', { report_id: reportId });
    return response.data;
  },

  async fetchSuperuserPrimeReports(): Promise<SuperuserPrimeReport[]> {
    const response = await api.get('/api/superuser/prime-reports');
    return Array.isArray(response.data?.reports) ? response.data.reports : [];
  },

  async createSuperuserPrimeReport(payload: {
    title: string;
    description?: string;
    token_cost: number;
    category?: string;
    google_drive_link: string;
    is_active?: boolean;
  }) {
    const response = await api.post('/api/superuser/prime-reports', payload);
    return response.data;
  },

  async updateSuperuserPrimeReport(reportId: number, payload: {
    title: string;
    description?: string;
    token_cost: number;
    category?: string;
    google_drive_link: string;
    is_active?: boolean;
  }) {
    const response = await api.patch(`/api/superuser/prime-reports/${reportId}`, payload);
    return response.data;
  },

  async deleteSuperuserPrimeReport(reportId: number) {
    const response = await api.delete(`/api/superuser/prime-reports/${reportId}`);
    return response.data;
  },

  // AI Chatbot (unified endpoint: supports model = gemini-2.0-flash, gpt-4o, claude-sonnet-4-5, etc.)
  async sendChatbotQuery(request: ChatbotRequest): Promise<ChatbotResponse> {
    const response = await api.post('/chatbot', request);
    return response.data;
  },

  async fetchSubscriptionPlans(): Promise<any[]> {
    const response = await api.get('/api/superuser/subscription-plans');
    return response.data?.plans || [];
  },

  async fetchSuperuserDashboardStats(page = 0): Promise<any> {
    const response = await api.get('/api/superuser/dashboard-stats', {
      params: { page },
    });
    return response.data;
  },

  async fetchSuperuserEmailAlertsOrgs(): Promise<{
    success: boolean;
    organizations?: Array<{
      org_id: number;
      org_name: string;
      digest_emails_enabled: boolean;
      has_active_subscription: boolean;
    }>;
    error?: string;
  }> {
    const response = await api.get('/api/superuser/email-alerts');
    return response.data;
  },

  async fetchSuperuserOrganizationsDirectory(): Promise<{
    success: boolean;
    organizations?: Array<{
      org_id: number;
      org_name: string;
      access_active: boolean;
      subscription_status: string;
      subscription_end_date: string | null;
      plan_name: string | null;
      subscription_id: number | null;
      digest_emails_enabled: boolean;
      token_balance?: number;
      user_count?: number;
    }>;
    error?: string;
  }> {
    const response = await api.get('/api/superuser/organizations-directory');
    return response.data;
  },

  async fetchSuperuserOrgUsers(orgId: number): Promise<{
    success: boolean;
    org_id?: number;
    org_name?: string;
    token_balance?: number;
    user_limit?: {
      current_users?: number;
      max_users?: number;
      remaining_slots?: number;
      can_add_user?: boolean;
    };
    users?: Array<{
      id: number;
      email: string;
      role: string;
      is_active: boolean;
      title?: string | null;
      created_at?: string | null;
    }>;
    error?: string;
  }> {
    const response = await api.get(`/api/superuser/organizations/${orgId}/users`);
    return response.data;
  },

  async addSuperuserOrgUser(
    orgId: number,
    payload: {
      email: string;
      role?: 'user' | 'admin' | 'superuser';
      title?: string;
      force_move?: boolean;
    },
  ): Promise<{
    success: boolean;
    created?: boolean;
    moved?: boolean;
    temp_password?: string | null;
    needs_force_move?: boolean;
    current_org_name?: string | null;
    user?: {
      id: number;
      email: string;
      role: string;
      is_active: boolean;
      title?: string | null;
    };
    error?: string;
  }> {
    const response = await api.post(`/api/superuser/organizations/${orgId}/users`, payload);
    return response.data;
  },

  async deactivateSuperuserOrgUser(
    orgId: number,
    userId: number,
  ): Promise<{
    success: boolean;
    user_id?: number;
    email?: string;
    is_active?: boolean;
    error?: string;
  }> {
    const response = await api.delete(`/api/superuser/organizations/${orgId}/users/${userId}`);
    return response.data;
  },

  async reactivateSuperuserOrgUser(
    orgId: number,
    userId: number,
  ): Promise<{
    success: boolean;
    user_id?: number;
    email?: string;
    is_active?: boolean;
    error?: string;
  }> {
    const response = await api.post(
      `/api/superuser/organizations/${orgId}/users/${userId}/reactivate`,
    );
    return response.data;
  },

  async topUpSuperuserOrgTokens(
    orgId: number,
    payload: { amount: number; notes?: string },
  ): Promise<{
    success: boolean;
    org_id?: number;
    amount?: number;
    previous_balance?: number;
    token_balance?: number;
    notes?: string;
    error?: string;
  }> {
    const response = await api.post(`/api/superuser/organizations/${orgId}/top-up-tokens`, payload);
    return response.data;
  },

  async getOrgNewDataDigestSettings(orgId: number): Promise<{
    success: boolean;
    new_data_digest_enabled?: boolean;
    has_active_subscription?: boolean;
    error?: string;
  }> {
    const response = await api.get(`/api/superuser/org-new-data-digest/${orgId}`);
    return response.data;
  },

  async patchOrgNewDataDigestSettings(
    orgId: number,
    new_data_digest_enabled: boolean,
  ): Promise<{ success: boolean; new_data_digest_enabled?: boolean; error?: string }> {
    const response = await api.patch(`/api/superuser/org-new-data-digest/${orgId}`, {
      new_data_digest_enabled,
    });
    return response.data;
  },

  async fetchSocIndications(): Promise<any[]> {
    const response = await api.get('/api/superuser/soc-indications');
    return response.data?.indications || [];
  },

  async fetchEmergingReportCatalog(): Promise<any[]> {
    const response = await api.get('/api/superuser/emerging-reports');
    return response.data?.reports || [];
  },

  async createTrialOnboarding(payload: {
    org_name: string;
    admin_email: string;
    admin_title?: string;
    plan_id: number | string;
    token_count: number;
    trial_days: number;
    allow_fda_approvals: boolean;
    additional_users?: Array<{ email: string; title?: string }>;
  }): Promise<any> {
    const response = await api.post('/api/superuser/onboarding', payload);
    return response.data;
  },

  async fetchOngoingTrials(): Promise<any> {
    const response = await api.get('/api/ongoing-trials');
    return response.data;
  },

  async fetchSuperuserOngoingTrials(params?: { search?: string; limit?: number }): Promise<any> {
    const response = await api.get('/api/superuser/ongoing-trials', { params });
    return response.data;
  },

  async setOngoingTrialVisibility(payload: { trial_id: number; mark: boolean; review_notes?: string }) {
    const response = await api.post('/api/superuser/ongoing-trials/visibility', payload);
    return response.data;
  },

  /** Remove this indication id from the trial's indication_codes (draft cleanup; trial may remain for other indications). */
  async unlinkEmergingTrialFromIndication(payload: {
    trial_id: number;
    indication_code: number | string;
    /** Appended after "Previously scraped for {indication}" on 1emerging_trials.irrelevant_reason (optional). */
    unlink_note?: string;
  }) {
    const response = await api.post('/api/superuser/emerging-trial-unlink-indication', payload);
    return response.data;
  },

  async setEmergingTrialIrrelevant(payload: {
    trial_id: number;
    irrelevant: boolean;
    reason?: string;
    indication_code?: number | string;
  }) {
    const response = await api.post('/api/superuser/emerging-trial-irrelevant', payload);
    return response.data;
  },

  async renameOngoingTrial(payload: {
    trial_id: number;
    trial_name: string;
    full_title?: string;
    indication_details?: string;
    sponsor?: string;
  }) {
    const response = await api.post('/api/superuser/ongoing-trials/rename', payload);
    return response.data;
  },

  async deleteOngoingTrial(payload: { trial_id: number }) {
    const response = await api.post('/api/superuser/ongoing-trials/delete', payload);
    return response.data;
  },

  async categorizeOngoingTrial(payload: { indication_name: string; indication_code?: string }) {
    const response = await api.post('/api/superuser/ongoing-trials/categorize', payload);
    return response.data;
  },

  async updateOngoingTrialAsset(payload: { trial_id: number; asset_id?: number | null }) {
    const response = await api.post('/api/superuser/ongoing-trials/link-asset', payload);
    return response.data;
  },

  async setOngoingTrialIndications(payload: { trial_id: number; indication_codes: number[] }) {
    const response = await api.post('/api/superuser/ongoing-trials/set-indications', payload);
    return response.data;
  },

  async fetchEmergingAssetLinks(assetId: number) {
    const response = await api.get(`/api/superuser/emerging-assets/${assetId}/links`);
    return response.data;
  },

  async fetchSuperuserFdaApprovals(params?: { search?: string; unlinked_only?: boolean; limit?: number }) {
    const response = await api.get('/api/superuser/fda-approvals', { params });
    return response.data;
  },

  async linkFdaApprovalToAsset(payload: {
    message_id: string;
    asset_id?: number;
    indication_code?: number;
    clear?: boolean;
  }) {
    const response = await api.post('/api/superuser/fda-approvals/link-asset', payload);
    return response.data;
  },

  async fetchEmergingSourceRows(_ongoingTrialId: number): Promise<{ success: boolean; data: EmergingSourceRow[] }> {
    return { success: true, data: [] };
  },

  async updateEmergingSourceRow(_sourceRowId: number, _payload: Record<string, any>) {
    return { success: false, error: 'Trial source rows are no longer stored.' };
  },

  async deleteEmergingSourceRow(_sourceRowId: number) {
    return { success: false, error: 'Trial source rows are no longer stored.' };
  },

  async lookupIndicationCode(code: string) {
    const response = await api.get('/api/superuser/indication-codes/lookup', {
      params: { code },
    });
    return response.data;
  },

  async lookupIndicationName(name: string) {
    const response = await api.get('/api/superuser/indication-codes/lookup-name', {
      params: { name },
    });
    return response.data;
  },

  async fetchOngoingTrialFavorites(): Promise<any> {
    const response = await api.get('/api/ongoing-trials/favorites');
    return response.data;
  },

  async updateOngoingTrialFavorite(payload: {
    indication_code: string;
    indication_name: string;
    action: 'add' | 'remove';
  }): Promise<any> {
    const response = await api.post('/api/ongoing-trials/favorites', payload);
    return response.data;
  },

  // Authentication
  async login(email: string, password: string) {
    const response = await api.post('/api/login', { email, password });
    return response.data;
  },

  async logout() {
    try {
      // Try the API endpoint first (better for React)
      const apiResponse = await fetch('/api/logout', {
        method: 'POST',
        credentials: 'include',
      });
      
      if (apiResponse.ok) {
        const data = await apiResponse.json();
        return data;
      }
      
      // Fallback to the regular logout route
      const response = await fetch('/logout', {
        method: 'GET',
        credentials: 'include',
        redirect: 'manual',
      });
      
      // If we get a redirect (3xx), that's expected
      if (response.status >= 300 && response.status < 400) {
        return { success: true };
      }
      
      return { success: true };
    } catch (err) {
      console.error('Logout error:', err);
      // Still return success to allow frontend to clear state
      return { success: true };
    }
  },

  async getCurrentUser() {
    const response = await api.get('/api/user');
    return response.data;
  },

  async fetchCompanies(): Promise<string[]> {
    const response = await api.get('/api/companies');
    return response.data?.companies || [];
  },

  // Account operations
  async updateProfile(data: { email: string; title?: string }) {
    const formData = new FormData();
    formData.append('email', data.email);
    if (data.title) {
      formData.append('title', data.title);
    }
    formData.append('profile_submit', 'true');
    
    // Get CSRF token
    const csrfResponse = await api.get('/api/csrf-token');
    if (csrfResponse.data.csrf_token) {
      formData.append('csrf_token', csrfResponse.data.csrf_token);
    }
    
    const response = await fetch('/account', {
      method: 'POST',
      body: formData,
      credentials: 'include',
    });
    
    if (!response.ok) {
      const text = await response.text();
      throw new Error(text.includes('already registered') ? 'Email already registered' : 'Failed to update profile');
    }
    
    return { success: true };
  },

  async changePassword(data: { current_password: string; new_password: string }) {
    const formData = new FormData();
    formData.append('current_password', data.current_password);
    formData.append('new_password', data.new_password);
    formData.append('confirm_password', data.new_password);
    formData.append('password_submit', 'true');
    
    // Get CSRF token
    const csrfResponse = await api.get('/api/csrf-token');
    if (csrfResponse.data.csrf_token) {
      formData.append('csrf_token', csrfResponse.data.csrf_token);
    }
    
    const response = await fetch('/account', {
      method: 'POST',
      body: formData,
      credentials: 'include',
    });
    
    if (!response.ok) {
      const text = await response.text();
      throw new Error(text.includes('incorrect') ? 'Current password is incorrect' : 'Failed to change password');
    }
    
    return { success: true };
  },

  async updateMarketingPreferences(data: { marketing_opt_out: boolean }) {
    const formData = new FormData();
    formData.append('marketing_opt_out', data.marketing_opt_out ? 'true' : 'false');
    formData.append('marketing_submit', 'true');
    
    // Get CSRF token
    const csrfResponse = await api.get('/api/csrf-token');
    if (csrfResponse.data.csrf_token) {
      formData.append('csrf_token', csrfResponse.data.csrf_token);
    }
    
    const response = await fetch('/account', {
      method: 'POST',
      body: formData,
      credentials: 'include',
    });
    
    if (!response.ok) {
      throw new Error('Failed to update email preferences');
    }
    
    return { success: true };
  },

  // Password reset
  async requestPasswordReset(email: string) {
    const formData = new FormData();
    formData.append('email', email);
    
    // Get CSRF token
    const csrfResponse = await api.get('/api/csrf-token');
    if (csrfResponse.data.csrf_token) {
      formData.append('csrf_token', csrfResponse.data.csrf_token);
    }
    
    const response = await fetch('/api/reset-password-request', {
      method: 'POST',
      body: formData,
      credentials: 'include',
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Failed to request password reset' }));
      throw { response: { status: response.status, data: errorData } };
    }
    
    return { success: true };
  },

  async resetPassword(token: string, password: string) {
    const formData = new FormData();
    formData.append('password', password);
    formData.append('confirm_password', password);
    
    // Get CSRF token
    const csrfResponse = await api.get('/api/csrf-token');
    if (csrfResponse.data.csrf_token) {
      formData.append('csrf_token', csrfResponse.data.csrf_token);
    }
    
    const response = await fetch(`/api/reset-password/${token}`, {
      method: 'POST',
      body: formData,
      credentials: 'include',
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Failed to reset password' }));
      throw { response: { status: response.status, data: errorData } };
    }
    
    return { success: true };
  },
  
  // Catalyst Calendar
  async fetchCatalysts(assetId?: number): Promise<Catalyst[]> {
    try {
      const response = await api.get('/api/catalyst-calendar', {
        params: assetId ? { asset_id: assetId } : undefined,
      });
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('Error fetching catalysts:', error);
      return [];
    }
  },

  async fetchEmergingAsset(assetId: number): Promise<EmergingAssetSummary | null> {
    try {
      const response = await api.get(`/api/emerging-asset/${assetId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching emerging asset:', error);
      return null;
    }
  },

  async patchEmergingAsset(
    assetId: number,
    body: Partial<{ asset_name: string; other_names: string | null; company_id: number | null }>
  ): Promise<{ success?: boolean; error?: string } & Partial<EmergingAssetSummary>> {
    const response = await api.patch(`/api/emerging-asset/${assetId}`, body);
    return response.data;
  },

  async fetchCompanySelectOptions(): Promise<{ id: number; company_name: string }[]> {
    try {
      const response = await api.get('/api/companies/select-options');
      return Array.isArray(response.data?.companies) ? response.data.companies : [];
    } catch {
      return [];
    }
  },

  async fetchCompanyEmergingAssets(companyId: number): Promise<EmergingCompanyAssetsResponse | null> {
    try {
      const response = await api.get(`/api/companies/${companyId}/emerging-assets`);
      return response.data?.success ? response.data : null;
    } catch (error) {
      console.error('Error fetching company emerging assets:', error);
      return null;
    }
  },

  async fetchEmergingTrial(trialId: number): Promise<{ success: boolean; data?: EmergingTrialDetail; error?: string }> {
    try {
      const response = await api.get(`/api/emerging-trial/${trialId}`);
      return response.data;
    } catch (error: unknown) {
      const e = error as { response?: { data?: { error?: string } }; message?: string };
      return { success: false, error: e?.response?.data?.error || e?.message || 'Failed to load trial' };
    }
  },

  async fetchCompanyDetail(companyId: number): Promise<{ id: number; company_name: string; stock_ticker?: string | null; company_website?: string | null } | null> {
    try {
      const response = await api.get(`/api/company/${companyId}`);
      return response.data?.success ? response.data.data : null;
    } catch {
      return null;
    }
  },
  
  async createCatalyst(catalyst: Partial<Catalyst>): Promise<Catalyst> {
    const response = await api.post('/api/catalyst-calendar', catalyst);
    return response.data;
  },
  
  async updateCatalyst(id: string, updates: Partial<Catalyst>): Promise<Catalyst> {
    const response = await api.put(`/api/catalyst-calendar/${id}`, updates);
    return response.data;
  },
  
  async deleteCatalyst(id: string): Promise<void> {
    await api.delete(`/api/catalyst-calendar/${id}`);
  },
  
  // Catalyst Favorites
  async fetchCatalystFavorites(): Promise<CatalystFavorite[]> {
    try {
      const response = await api.get('/api/catalyst-favorites');
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('Error fetching catalyst favorites:', error);
      return [];
    }
  },
  
  async createCatalystFavorite(favoriteType: 'asset' | 'mechanism' | 'target', favoriteValue: string): Promise<CatalystFavorite> {
    const response = await api.post('/api/catalyst-favorites', {
      favorite_type: favoriteType,
      favorite_value: favoriteValue
    });
    return response.data;
  },
  
  async deleteCatalystFavorite(favoriteId: number): Promise<void> {
    await api.delete(`/api/catalyst-favorites/${favoriteId}`);
  },

  // Alert digest (emerging report updates + NCCN version)
  async getAlertDigestSubscriptions(): Promise<{ success: boolean; subscriptions: AlertDigestSubscription[] }> {
    const response = await api.get('/api/alert-digest/subscriptions');
    return response.data;
  },
  async addAlertDigestSubscription(payload: {
    subscription_type: 'emerging' | 'nccn';
    indication_code?: string;
    indication_name?: string;
    digest_frequency?: 'daily' | 'weekly';
  }): Promise<{ success: boolean }> {
    const response = await api.post('/api/alert-digest/subscriptions', payload);
    return response.data;
  },
  async removeAlertDigestSubscription(subscriptionId: number): Promise<{ success: boolean }> {
    const response = await api.delete(`/api/alert-digest/subscriptions/${subscriptionId}`);
    return response.data;
  },
  async updateAlertDigestSubscription(subscriptionId: number, digest_frequency: 'daily' | 'weekly'): Promise<{ success: boolean }> {
    const response = await api.patch(`/api/alert-digest/subscriptions/${subscriptionId}`, { digest_frequency });
    return response.data;
  },

  // Scraping (superuser only)
  async scrapeCatalysts(payload: {
    textParts?: Array<{ name: string; content: string }>;
    images?: Array<{ data: string; mimeType: string }>;
    rawText?: string;
    userInstructions?: string;
  }): Promise<{ success: boolean; message: string; catalysts_created: number }> {
    const response = await api.post('/api/catalyst-calendar/scrape', payload);
    return response.data;
  },

  // ---------------- Asset Radar ----------------
  // Thin wrappers over asset_radar/api.py's /asset-radar/api/* JSON endpoints (read-only here; the
  // backend/APIs already work and are out of scope to change). See frontend/src/components/assetRadar/.
  //
  // workbooks/timeline/trial-detail are each confirmed 0.5-5s server-side (real backend work -- e.g.
  // /api/timeline rebuilds the whole indication's trial matching and also upserts a research queue, so
  // it's not something to just skip or call less often). Switching between indications/sub-indications
  // re-triggers these, so without caching every switch pays that cost again, including re-visiting a
  // setting the user already viewed a moment ago. These session-lifetime caches (never invalidated,
  // cleared only by a full page reload) make repeat switches instant without touching that backend work.
  async arGetDiseases(): Promise<ArDisease[]> {
    const response = await api.get('/asset-radar/api/diseases');
    return Array.isArray(response.data) ? response.data : [];
  },
  async arGetWorkbook(fileId: string): Promise<ArWorkbookParsed> {
    const cached = arWorkbookCache.get(fileId);
    if (cached) return cached;
    const inflight = arWorkbookInflight.get(fileId);
    if (inflight) return inflight;
    const promise = (async () => {
      const response = await api.get(`/asset-radar/api/workbooks/${encodeURIComponent(fileId)}`);
      const data = normalizeArWorkbook(response.data);
      arWorkbookCache.set(fileId, data);
      return data;
    })();
    arWorkbookInflight.set(fileId, promise);
    try { return await promise; } finally { arWorkbookInflight.delete(fileId); }
  },
  // /api/index-all: every synced disease's fully-parsed workbook in one call, for the "By target"
  // cross-disease asset explorer. Cached for the session the same way a single workbook is -- it's the
  // same payload shape repeated per disease, just assembled server-side instead of N client fetches.
  async arGetIndexAll(): Promise<ArIndexAllEntry[]> {
    if (arIndexAllCache) return arIndexAllCache;
    if (arIndexAllInflight) return arIndexAllInflight;
    arIndexAllInflight = (async () => {
      const response = await api.get('/asset-radar/api/index-all');
      const data: ArIndexAllEntry[] = Array.isArray(response.data) ? response.data : [];
      for (const entry of data) entry.parsed = normalizeArWorkbook(entry.parsed);
      arIndexAllCache = data;
      return data;
    })();
    try { return await arIndexAllInflight; } finally { arIndexAllInflight = null; }
  },
  async arListLists(): Promise<ArListsResponse> {
    const response = await api.get('/asset-radar/api/lists');
    return response.data;
  },
  async arCreateList(kind: ArListKind, name: string, assets: string[] = []): Promise<ArList> {
    const response = await api.post('/asset-radar/api/lists', { kind, name, assets });
    return response.data;
  },
  async arPatchList(listId: number, patch: Partial<Pick<ArList, 'name' | 'icon' | 'assets' | 'labels'>>): Promise<ArList> {
    const response = await api.patch(`/asset-radar/api/lists/${listId}`, patch);
    return response.data;
  },
  async arDeleteList(listId: number): Promise<void> {
    await api.delete(`/asset-radar/api/lists/${listId}`);
  },
  async arCopyList(listId: number): Promise<ArList> {
    const response = await api.post(`/asset-radar/api/lists/${listId}/copy`, {});
    return response.data;
  },
  async arGetTracker(): Promise<ArTrackerAsset[]> {
    const response = await api.get('/asset-radar/api/tracker');
    return Array.isArray(response.data) ? response.data : [];
  },
  async arGetTimeline(disease: string, setting: string, fileId?: string): Promise<ArTimelineResponse> {
    const key = `${disease}::${setting}::${fileId || ''}`;
    const cached = arTimelineCache.get(key);
    if (cached) return cached;
    const inflight = arTimelineInflight.get(key);
    if (inflight) return inflight;
    const promise = (async () => {
      const response = await api.get('/asset-radar/api/timeline', {
        params: { disease, setting, file_id: fileId || undefined },
      });
      arTimelineCache.set(key, response.data);
      return response.data;
    })();
    arTimelineInflight.set(key, promise);
    try { return await promise; } finally { arTimelineInflight.delete(key); }
  },
  async arGetRegistry(ids: string[]): Promise<ArRegistryBatchResponse> {
    const response = await api.get('/asset-radar/api/registry', { params: { ids: ids.join(',') } });
    return response.data;
  },
  async arGetTrial(nct: string): Promise<ArRegistryDetailResponse> {
    const cached = arTrialCache.get(nct);
    if (cached) return cached;
    const inflight = arTrialInflight.get(nct);
    if (inflight) return inflight;
    const promise = (async () => {
      const response = await api.get(`/asset-radar/api/registry/${encodeURIComponent(nct)}`);
      // Don't cache a "pending" result (CT.gov lookup still queued server-side) -- it's meant to be
      // re-polled and will have real data soon, unlike ready/missing/error which are stable for the
      // session.
      if (!(response.data && (response.data as any).pending)) arTrialCache.set(nct, response.data);
      return response.data;
    })();
    arTrialInflight.set(nct, promise);
    try { return await promise; } finally { arTrialInflight.delete(nct); }
  },
  /** Clear the workbook/timeline/trial-detail caches above -- call after a sync completes, since it
   * loads fresh data that the cached responses would otherwise hide until a full page reload. */
  arClearRadarCache(): void {
    arWorkbookCache.clear();
    arTimelineCache.clear();
    arTrialCache.clear();
    arIndexAllCache = null;
  },
  async arGetCatalysts(): Promise<Record<string, unknown>[]> {
    const response = await api.get('/asset-radar/api/catalysts');
    return Array.isArray(response.data) ? response.data : [];
  },
  async arGetGeoWmap(): Promise<ArGeoWmap> {
    const response = await api.get('/asset-radar/geo/wmap.json');
    return response.data;
  },
  async arGetGeoLookups(): Promise<ArGeoLookups> {
    const response = await api.get('/asset-radar/geo/lookups.json');
    return response.data;
  },
  async arGetGeoHi(): Promise<ArGeoHi> {
    const response = await api.get('/asset-radar/geo/wmhi.json');
    return response.data;
  },
  async arGetGeoCity(): Promise<ArGeoCityRaw> {
    const response = await api.get('/asset-radar/geo/wmcity.json');
    return response.data;
  },
  async arGetGeoCC(): Promise<ArGeoCC> {
    const response = await api.get('/asset-radar/geo/wmcc.json');
    return response.data;
  },
  async arStartSync(force = false): Promise<{ run_id: number }> {
    const response = await api.post('/asset-radar/api/sync', force ? { force: true } : {});
    return response.data;
  },
  async arGetLatestSync(): Promise<{ id: number; status: string; stats: Record<string, unknown>; errors: unknown[]; started_at: string | null; finished_at: string | null } | null> {
    const response = await api.get('/asset-radar/api/sync/latest');
    return response.data;
  },
};

export default api;
